import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { CONTACT } from "../../data";

const BANNER_URL = "/banner-home.webp";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const COMMERCIAL_URL =
  "https://customer-assets-jt897jd0.emergentagent.net/job_legacy-secure-12/artifacts/qv72nev1_224%29354-1304.mp4";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const yShield = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* parallax gold texture */}
      <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0 opacity-[0.14]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1545873509-33e944ca7655?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1a35]/75 via-[#081226]/90 to-[#081226]" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% -20%, rgba(212,175,55,0.16), rgba(8,18,38,0) 60%)" }} />
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 h-[70vh] w-[70vh] rounded-full bg-[#bf953f]/10 blur-[140px]" />
        <div className="absolute top-1/4 -right-1/4 h-[60vh] w-[60vh] rounded-full bg-[#1a4b9b]/20 blur-[160px]" />
        <div className="absolute -bottom-1/4 -left-1/4 h-[50vh] w-[50vh] rounded-full bg-[#1a4b9b]/10 blur-[160px]" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full pt-24 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="mb-8 flex justify-center"
        >
          <img
            src={BANNER_URL}
            alt="Heritage Family Protection Agency"
            data-testid="hero-banner"
            className="w-full h-auto rounded-2xl border border-[#bf953f]/25 shadow-[0_0_60px_rgba(191,149,63,0.15)]"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mb-10 flex justify-center"
        >
          <video
            src={COMMERCIAL_URL}
            data-testid="hero-commercial"
            controls
            playsInline
            preload="metadata"
            className="w-full max-w-4xl h-auto rounded-2xl border border-[#bf953f]/25 shadow-[0_0_60px_rgba(191,149,63,0.15)] bg-black"
          />
        </motion.div>

        <h1 className="font-serif-display font-medium tracking-tight text-white leading-[1.05] mt-2" data-testid="hero-heading">
          {["Protecting Families,", "Preserving Legacies."].map((t, i) => (
            <span key={i} className="block overflow-hidden pb-[0.08em]">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className={`block ${
                  i === 1
                    ? "text-gold-gradient italic text-3xl sm:text-4xl md:text-5xl lg:text-6xl mt-1 pb-3 pr-2"
                    : "text-5xl sm:text-6xl md:text-7xl lg:text-8xl"
                }`}
              >
                {t}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 1 }}
          className="mt-8 max-w-xl font-body font-light text-base md:text-lg leading-relaxed text-white/65"
        >
          A full-shield agency safeguarding what you've built — from your first policy to your final wishes.
          One team. Six pillars of protection. A legacy that outlives you.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#services"
            data-testid="hero-explore-btn"
            className="btn-sweep inline-flex items-center rounded-full bg-gold-metallic px-8 py-3.5 font-body text-sm font-medium tracking-wide text-black"
          >
            Explore Our Protection
          </a>
          <a
            href={CONTACT.phoneHref}
            data-testid="hero-call-btn"
            className="inline-flex items-center rounded-full border border-[#bf953f]/40 px-8 py-3.5 font-body text-sm tracking-wide text-white/80 hover:text-[#fcf6ba] hover:border-[#d4af37] transition-colors duration-300"
          >
            Speak With an Advisor
          </a>
        </motion.div>
      </div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#d4af37]/70"
      >
        <span className="font-body text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <ChevronDown size={18} className="animate-float-slow" />
      </motion.div>
    </section>
  );
};
